/**
 * PartnerAssignmentsView Component
 * Full view for managing expert assignments
 */

import { useState } from 'react';
import { Plus, Users, CheckCircle } from 'lucide-react';
import { PartnerAssignment, AssignmentFormData } from '@/types/partner-assignments.types';
import { PARTNER_TRANSLATIONS } from '@/constants/partner-assignments.constants';
import { useLanguage } from '@/contexts/LanguageContext';
import { usePartnerAssignments } from '@/hooks/usePartnerAssignments';
import { AssignmentCard } from './AssignmentCard';
import { AssignExpertDialog } from './AssignExpertDialog';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Skeleton } from '@/components/ui/skeleton';

interface PartnerAssignmentsViewProps {
  dealId?: string;
  buyerId?: string;
  sellerId?: string;
  isExpert?: boolean;
  showHeader?: boolean;
}

export function PartnerAssignmentsView({ 
  dealId,
  buyerId,
  sellerId,
  isExpert = false,
  showHeader = true,
}: PartnerAssignmentsViewProps) {
  const { language } = useLanguage();
  const t = PARTNER_TRANSLATIONS[language === 'fr' ? 'fr' : 'en'];
  
  const {
    assignments,
    experts,
    loading,
    createAssignment,
    updateAssignmentStatus,
    getActiveAssignments,
    getCompletedAssignments,
  } = usePartnerAssignments(dealId);

  const [isDialogOpen, setIsDialogOpen] = useState(false);

  const activeAssignments = getActiveAssignments();
  const completedAssignments = getCompletedAssignments();

  const handleSubmit = async (data: AssignmentFormData) => {
    await createAssignment(data);
  };

  const handleAccept = async (id: string) => {
    await updateAssignmentStatus(id, 'accepted');
  };

  const handleDecline = async (id: string) => {
    await updateAssignmentStatus(id, 'cancelled');
  };

  const handleComplete = async (id: string) => {
    await updateAssignmentStatus(id, 'completed');
  };

  const handleOpenChat = (assignment: PartnerAssignment) => {
    // Navigate to chat or open chat modal
    console.log('Open chat for assignment:', assignment.id);
  };

  const handleScheduleCall = (assignment: PartnerAssignment) => {
    // Navigate to calendar or open schedule dialog
    console.log('Schedule call for assignment:', assignment.id);
  };

  if (loading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-10 w-40" />
        <Skeleton className="h-32 w-full" />
        <Skeleton className="h-32 w-full" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {showHeader && (
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold tracking-tight">{t.title}</h1>
            <p className="text-muted-foreground">{t.subtitle}</p>
          </div>
          {!isExpert && dealId && (
            <Button onClick={() => setIsDialogOpen(true)}>
              <Plus className="w-4 h-4 mr-2" />
              {t.assignExpert}
            </Button>
          )}
        </div>
      )}

      <Tabs defaultValue="active" className="w-full">
        <TabsList>
          <TabsTrigger value="active" className="gap-2">
            <Users className="w-4 h-4" />
            {t.activeAssignments} ({activeAssignments.length})
          </TabsTrigger>
          <TabsTrigger value="completed" className="gap-2">
            <CheckCircle className="w-4 h-4" />
            {t.completedAssignments} ({completedAssignments.length})
          </TabsTrigger>
        </TabsList>

        <TabsContent value="active" className="mt-4">
          {activeAssignments.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center">
                <Users className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
                <p className="text-muted-foreground">{t.noAssignments}</p>
                {!isExpert && dealId && (
                  <Button 
                    variant="outline" 
                    className="mt-4"
                    onClick={() => setIsDialogOpen(true)}
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    {t.assignExpert}
                  </Button>
                )}
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-3">
              {activeAssignments.map((assignment) => (
                <AssignmentCard
                  key={assignment.id}
                  assignment={assignment}
                  isExpert={isExpert}
                  onAccept={handleAccept}
                  onDecline={handleDecline}
                  onComplete={handleComplete}
                  onOpenChat={handleOpenChat}
                  onScheduleCall={handleScheduleCall}
                />
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="completed" className="mt-4">
          {completedAssignments.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center">
                <p className="text-muted-foreground">{t.noAssignments}</p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-3">
              {completedAssignments.map((assignment) => (
                <AssignmentCard
                  key={assignment.id}
                  assignment={assignment}
                  isExpert={isExpert}
                />
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Assign Expert Dialog */}
      {dealId && (
        <AssignExpertDialog
          open={isDialogOpen}
          onOpenChange={setIsDialogOpen}
          onSubmit={handleSubmit}
          experts={experts}
          dealId={dealId}
          buyerId={buyerId}
          sellerId={sellerId}
        />
      )}
    </div>
  );
}
